/**
 * Headless Smarty US Autocomplete Pro combobox logic.
 *
 * `AddressAutocompleteInput`, the M3P review step and the admin's
 * `AddressTextField` share it.
 *
 *  - Lookups go from the browser straight to Smarty with the embedded key;
 *    there is no backend proxy.
 *  - It only suggests; the caller validates the fields.
 *  - Manual entry always works: an empty key, a Smarty outage, or no matches
 *    leaves the field behaving exactly like a plain input. Lookup errors are
 *    swallowed quietly (`console.debug` at most).
 *  - A selection fills street1, city, state and zip. `street2` stays manual;
 *    there is no secondary (per-unit, `selected=`) expansion.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { ClientBuilder, SharedCredentials, usAutocompletePro } from "smartystreets-javascript-sdk";

/** The fields a Smarty selection fills; `street2` stays manual. */
export interface AddressSuggestion {
  street1: string;
  city: string;
  /** Two-letter USPS state code, matching the `US_STATES` `<Select>` options. */
  state: string;
  /** 5-digit ZIP. */
  zip: string;
}

/** One dropdown row: the mapped fields plus the human-readable option label. */
export interface AddressSuggestionItem {
  label: string;
  suggestion: AddressSuggestion;
}

/** ARIA combobox attributes to spread onto the text input (empty when inert). */
export interface ComboboxInputProps {
  role?: "combobox";
  "aria-expanded"?: boolean;
  "aria-autocomplete"?: "list";
  "aria-controls"?: string;
  "aria-activedescendant"?: string | undefined;
}

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

type UsAutocompleteProClient = ReturnType<ClientBuilder["buildUsAutocompleteProClient"]>;

/** One SDK client per embedded key for the lifetime of the page. */
const clientCache = new Map<string, UsAutocompleteProClient>();

function clientFor(embeddedKey: string): UsAutocompleteProClient {
  let client = clientCache.get(embeddedKey);
  if (!client) {
    client = new ClientBuilder(new SharedCredentials(embeddedKey)).buildUsAutocompleteProClient();
    clientCache.set(embeddedKey, client);
  }
  return client;
}

/** Map an SDK `Suggestion` to a dropdown row. */
function toItem(s: InstanceType<typeof usAutocompletePro.Suggestion>): AddressSuggestionItem {
  const secondary = s.secondary ? ` ${s.secondary}` : "";
  return {
    label: `${s.streetLine}${secondary}, ${s.city}, ${s.state} ${s.zipcode}`,
    suggestion: { street1: s.streetLine, city: s.city, state: s.state, zip: s.zipcode },
  };
}

export interface UseAddressAutocompleteOptions {
  /** Smarty embedded key. An empty key leaves the hook inert: no client, no fetches. */
  embeddedKey: string;
  onSelect: (suggestion: AddressSuggestion) => void;
  /** Mirrors the input's `disabled`; a disabled hook never looks up or opens. */
  disabled?: boolean | undefined;
}

export interface UseAddressAutocompleteReturn {
  /** False when the key is empty or the field is disabled; render a plain input then. */
  enabled: boolean;
  /** Dropdown visibility (loading, results, or the no-matches notice). */
  open: boolean;
  loading: boolean;
  items: AddressSuggestionItem[];
  /** Keyboard-highlighted option index; -1 = none. */
  activeIndex: number;
  listboxId: string;
  optionId: (index: number) => string;
  comboboxProps: ComboboxInputProps;
  /** Wire to the input's onChange with the current value. */
  handleInputChange: (value: string) => void;
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Wire to the input's onBlur. Closing on blur also covers outside clicks. */
  handleBlur: () => void;
  /** Select the i-th item (option click). */
  select: (index: number) => void;
}

export function useAddressAutocomplete({
  embeddedKey,
  onSelect,
  disabled,
}: UseAddressAutocompleteOptions): UseAddressAutocompleteReturn {
  const enabled = embeddedKey !== "" && !disabled;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AddressSuggestionItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Stale-response guard: each dispatched lookup captures the sequence number,
  // and a response (or error) whose number differs from the current one is dropped.
  const seqRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number): string => `${baseId}-option-${index}`;

  // Unmount: cancel the pending debounce and invalidate any in-flight lookup.
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      seqRef.current += 1;
    },
    [],
  );

  function close(): void {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    seqRef.current += 1;
    setOpen(false);
    setLoading(false);
    setItems([]);
    setActiveIndex(-1);
  }

  async function runLookup(query: string): Promise<void> {
    const seq = ++seqRef.current;
    try {
      const response = await clientFor(embeddedKey).send(new usAutocompletePro.Lookup(query));
      if (seq !== seqRef.current) return; // stale: a newer lookup or a close superseded it
      setItems(response.result.map(toItem));
      setLoading(false);
      setActiveIndex(-1);
    } catch (err) {
      // Silent degradation: manual entry keeps working, no error UI.
      console.debug("address autocomplete lookup failed", err);
      if (seq !== seqRef.current) return;
      setOpen(false);
      setLoading(false);
      setItems([]);
      setActiveIndex(-1);
    }
  }

  function handleInputChange(value: string): void {
    if (!enabled) return;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    const query = value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      close();
      return;
    }
    setOpen(true);
    setLoading(true);
    setActiveIndex(-1);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void runLookup(query);
    }, DEBOUNCE_MS);
  }

  function select(index: number): void {
    const item = items[index];
    if (!item) return;
    onSelect(item.suggestion);
    close();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation(); // don't also dismiss an enclosing modal
      close();
      return;
    }
    if (items.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault(); // selecting must not submit the surrounding form
      select(activeIndex);
    }
  }

  const comboboxProps: ComboboxInputProps = enabled
    ? {
        role: "combobox",
        "aria-expanded": open,
        "aria-autocomplete": "list",
        "aria-controls": listboxId,
        "aria-activedescendant": activeIndex >= 0 ? optionId(activeIndex) : undefined,
      }
    : {};

  return {
    enabled,
    open: enabled && open,
    loading,
    items,
    activeIndex,
    listboxId,
    optionId,
    comboboxProps,
    handleInputChange,
    handleKeyDown,
    handleBlur: () => {
      if (open) close();
    },
    select,
  };
}
